<script lang="ts" setup>
import { computed } from 'vue'
import type { MemberPageConfig } from '../configs/types'
import novelarcVipIcon from '../icons/novelarc/vip.png'
import { useUserStore } from '@/stores'
import { usePurchase, useSubscription } from '@/composables/payment'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { resources } from '@/config/brands'

interface Props {
  config: MemberPageConfig
}

const props = defineProps<Props>()

const userStore = useUserStore()
const { elementClick } = useElementClick()
const { subscribeProducts } = useSubscription('member')
const { templateProducts, loading } = usePurchase('member')

const topupShow = computed(() => {
  return templateProducts.value && templateProducts.value.length > 0
})

// 定义样式变量供 CSS v-bind 使用
const avatarBorderColor = props.config.theme.avatarBorder
const memberBgUrl = computed(() => `url(${props.config.resources.memberBg}) no-repeat top center`)
const topUpBtnBg = computed(() => props.config.theme.topUpBtnBg)
const subscribeBg = computed(() => props.config.theme.subscribeBg || 'linear-gradient(90deg, #57483E 0%, #312A24 100%)')
const subscribeText = computed(() => props.config.theme.subscribeText || 'linear-gradient(0deg, #FFC781 0%, #F8B57E 100%)')
const subscribeText2 = computed(() => props.config.theme.subscribeText2 || '#CAA37B')
const vipBtnBg = computed(() => props.config.theme.vipBtnBg || 'linear-gradient(90deg, #FFD8B0 0%, #F9B67D 100%)')
const vipBtnText = computed(() => props.config.theme.vipBtnText || '#270100')
const vipIcon = computed(() => props.config.resources.vipIcon || '/images/novelarc/vip-icon.png')
const memberVipIcon = computed(() => props.config.resources.memberVip || novelarcVipIcon)
const menuIcons = computed(() => ({
  transaction: props.config.menuIcons?.transaction || '/images/novelarc/member/icon-transaction.png',
  unlocked: props.config.menuIcons?.unlocked || '/images/novelarc/member/icon-unlock.png',
  contactUs: props.config.menuIcons?.contactUs || '/images/novelarc/member/icon-contact.png',
  setting: props.config.menuIcons?.setting || '/images/novelarc/member/icon-setting.png',
}))
</script>

<template>
  <div class="page-cover">
    <!-- 头像 -->
    <div class="px-30px pt-40px pb-27px flex items-center justify-between">
      <div class="shrink-0">
        <img v-if="userStore.$state.headImageUrl" class="w-15 h-15 border rounded-full avatar-border" :src="userStore.$state.thirdHeadImgUrl" alt="avatar">
        <img v-else class="w-15 h-15 rounded-full" :src="config.resources.userDefaultAvatar" alt="avatar">
      </div>

      <div class="flex-1 overflow-hidden px-12px">
        <div class="flex items-center width-full">
          <div class="username overflow-hidden whitespace-nowrap text-ellipsis">
            {{ userStore.$state.thirdNickName || userStore.$state.email || userStore.$state.memberName }}
          </div>
          <div v-if="userStore.$state.subscribe" class="shrink-0">
            <img :src="memberVipIcon" class="h-14px w-auto ml-8px">
          </div>
        </div>
        <p class="uid">
          UID {{ userStore.$state.code }}
        </p>
      </div>
    </div>

    <!-- vip 订阅 -->
    <div v-if="subscribeProducts?.length || userStore.$state.subscribe" class="vip-cover flex items-center justify-between mx-10px px-16px">
      <img :src="vipIcon" class="w-30px h-30px shrink-0" alt="vip icon">
      <div class="flex-1 ml-12px">
        <div class="vip-title">
          <span v-if="!userStore.$state.subscribe">{{ config.texts.getVip }}</span>
          <span v-else>{{ config.texts.vipMembership }}</span>
        </div>
        <div v-if="!userStore.$state.subscribe" class="vip-subtitle">
          {{ config.texts.moreCoinsMoreBenefits }}
        </div>
        <div v-if="userStore.$state.subscribeEndDate" class="vip-subtitle">
          {{ config.texts.expires }} {{ $d(new Date(userStore.$state.subscribeEndDate), 'short') }}
        </div>
      </div>
      <router-link
        v-if="!userStore.$state.subscribe"
        :to="{ name: 'subscribe' }"
      >
        <div class="details-btn flex items-center justify-center" @click="elementClick('Details')">
          {{ config.texts.details }}
        </div>
      </router-link>
    </div>

    <!-- 充值,金币信息 -->
    <div class="wallet-cover mx-10px flex justify-between items-center px-16px">
      <div>
        <div class="text-16px font-medium text-#32353A leading-14px">
          {{ config.texts.myWallet }}
        </div>
        <div class="flex items-center mt-8px">
          <img :src="resources?.coin" width="20px" height="20px" alt="金币图标">
          <strong class="text-#32353A ml-4px">{{ userStore.$state.coinBalance }}</strong>
          <span class="text-12px text-#6A6E7A ml-4px leading-14px">{{ config.texts.coins }}</span>
        </div>
      </div>
      <div v-if="!loading">
        <router-link :to="topupShow ? { name: 'pay' } : { name: 'welfare' }">
          <div class="get-btn flex items-center justify-center" @click="elementClick('TopUp')">
            {{ topupShow ? config.texts.topUp : config.texts.get }}
          </div>
        </router-link>
      </div>
    </div>

    <!-- 菜单：单卡片列表，每项一行（AureNovel 差异化样式） -->
    <div class="menu-card mt-10px mx-10px">
      <router-link
        :to="{ name: 'recharge' }"
        class="menu-item flex items-center"
      >
        <img :src="menuIcons.transaction" width="32px" height="32px" alt="">
        <p class="menu-item-text">
          {{ config.texts.transactionHistory }}
        </p>
      </router-link>

      <router-link
        :to="{ name: 'consume' }"
        class="menu-item flex items-center"
      >
        <img :src="menuIcons.unlocked" width="32px" height="32px" alt="">
        <p class="menu-item-text">
          {{ config.texts.chaptersUnlocked }}
        </p>
      </router-link>

      <router-link :to="{ name: 'contact-us' }" class="menu-item block">
        <div class="flex items-center" @click="elementClick('contact_us')">
          <img :src="menuIcons.contactUs" width="32px" height="32px" alt="">
          <p class="menu-item-text">
            {{ config.texts.contactUs }}
          </p>
        </div>
      </router-link>

      <router-link :to="{ name: 'setting' }" class="menu-item block">
        <div class="flex items-center" @click="elementClick('setting')">
          <img :src="menuIcons.setting" width="32px" height="32px" alt="">
          <p class="menu-item-text">
            {{ config.texts.setting }}
          </p>
        </div>
      </router-link>
    </div>

    <BasicTabbar tab="member" />
  </div>
</template>

<style scoped>
.page-cover {
  width: 100%;
  min-height: 100vh;
  background: v-bind(memberBgUrl);
  background-size: 100% 100%;
}

.avatar-border {
  border-color: v-bind(avatarBorderColor);
}

.vip-cover {
  width: 355px;
  height: 73px;
  background: v-bind(subscribeBg);
  border-radius: 12px;
  box-sizing: border-box;
}

.vip-title {
  font-weight: 500;
  font-size: 16px;
  line-height: 14px;
  background: v-bind(subscribeText);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.vip-subtitle {
  font-weight: 400;
  font-size: 12px;
  color: v-bind(subscribeText2);
  margin-top: 6px;
}

.details-btn {
  width: 80px;
  height: 32px;
  background: v-bind(vipBtnBg);
  border-radius: 16px;
  font-weight: 500;
  font-size: 14px;
  color: v-bind(vipBtnText);
}

.wallet-cover {
  width: 355px;
  height: 77px;
  background: linear-gradient(135deg, #FFFDF6 0%, #FFF3D8 100%);
  border: 1px solid #FFEFC2;
  border-radius: 12px;
  margin-top: 10px;
  box-sizing: border-box;
}

.get-btn {
  width: 80px;
  height: 32px;
  background: v-bind(topUpBtnBg);
  border-radius: 16px;
  font-weight: 500;
  font-size: 14px;
  color: #FFFFFF;
}

.username {
  font-weight: 500;
  font-size: 18px;
  color: #32353A;
  line-height: 14px;
}

.uid {
  font-weight: 400;
  font-size: 12px;
  color: #969CAA;
  line-height: 11px;
  margin-top: 8px;
}

/* 菜单卡片：单卡片内多行列表 */
.menu-card {
  background: #FFFFFF;
  border-radius: 12px;
  padding: 4px 14px;
  box-shadow: 0 2px 8px rgba(220, 224, 228, 0.35);
}

.menu-item {
  padding: 14px 0;
}

.menu-item + .menu-item {
  border-top: 1px solid #F7F8FA;
}

.menu-item-text {
  margin-left: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #32353A;
  line-height: 17px;
}
</style>
